import React, { useState } from "react";
import { Card, Row, Col, Affix, Alert } from "antd";
import LoadingIndicator from "../../components/shared/LoadingIndicator/LoadingIndicator";
import RefreshVisibilityAnalysisButton from "./RefreshVisibilityAnalysisButton";
import DownloadPdfButton from "./DownloadPdfButton";
import useDomainVisibilityAnalysis from "./hooks/useDomainVisibilityAnalysis";
import SearchPromptsList from "./SearchPromptsList";
import TopWebsites from "./TopWebsites";
import PresenceScore from "./PresenceScore";
import { ModelFamily } from "../types";
import { assistants } from "./assistants";
import LastCheckedDate from "../LastCheckedDate";
import "./VisibilityAnalysis.less";


const { Meta } = Card;
type Props = {
    domain: string;
    modelFamily: ModelFamily;
}

export default function VisibilityAnalysis({ domain, modelFamily }: Props) {
    const [, setIsRefreshingAnalysis] = useState(false);
    const { data: domainVisibilityAnalysis, isLoading, isInProgress, analysisError, trackAnalysisProcess } = useDomainVisibilityAnalysis(domain, modelFamily);
    const assistant = assistants[modelFamily];

    return (
        <Card
            className="llm-visibility-analysis"
            title={
                <Meta
                    title={"LLM Visibility Analysis"}
                    description={domainVisibilityAnalysis ? <LastCheckedDate checkedAt={domainVisibilityAnalysis.c_at} /> : null}
                />
            }
            extra={
                !isLoading ? (
                    <div className="analysis-actions">
                        <RefreshVisibilityAnalysisButton domain={domain} modelFamily={modelFamily} inProgress={isInProgress} setIsRefreshingAnalysis={setIsRefreshingAnalysis} onAnalysisStarted={trackAnalysisProcess} />
                        <DownloadPdfButton domain={domain} modelFamily={modelFamily} disabled={isInProgress || !domainVisibilityAnalysis} />
                    </div>
                ) : null
            }
        >
            {isLoading && <LoadingIndicator />}
            {!isLoading && isInProgress && (
                <>
                    <Alert
                        className="analysis-in-progress-alert"
                        type="info"
                        showIcon
                        message={
                            <div className="analysis-progress">
                                <div className="title">{assistant.label} Visibility Analysis in progress</div>
                                <div className="loader"></div>
                            </div>
                        }
                        description={
                            <>
                                <span className="alert-message">
                                    We are asking {assistant.label} the questions people ask about this market. This may take a couple of minutes, depending on the website's content complexity.&nbsp;
                                </span>
                                <span className="alert-message">
                                    Feel free to leave this page - the analysis will continue running in the background.
                                </span>
                            </>
                        }
                    />
                    <LoadingIndicator />
                </>
            )}

            {!isLoading && !isInProgress && !domainVisibilityAnalysis && (
                // One assistant made this state rare. Three make it ordinary: an assistant can fail
                // on a domain the other two answered, and saying nothing at all leaves an empty card.
                <Alert
                    type={analysisError ? "warning" : "info"}
                    showIcon
                    message={analysisError
                        ? `The ${assistant.label} analysis did not finish`
                        : `No ${assistant.label} analysis yet`
                    }
                    description={analysisError
                        ? `Something went wrong while ${assistant.label} was answering the questions. Press "Refresh analysis" to run it again. The other assistants are not affected.`
                        : `Press "Refresh analysis" to ask ${assistant.label} the questions people ask about this market.`
                    }
                />
            )}

            {!isLoading && !isInProgress && domainVisibilityAnalysis && (
                <Row gutter={24} style={{ paddingTop: "8px" }}>
                    <Col span={18} className="search-prompts-column">
                        <Alert
                            message={
                                <span>
                                    The <strong>LLM Visibility Analysis</strong> measures how often a website is recommended in AI-generated answers. To do that, it generates and analyzes queries from
                                    three categories: brand perception, specific-need, and broad market search queries. Instead of looking at traditional search engine rankings, this analysis reads the
                                    answers produced by LLMs and counts the questions where the website is recommended at all. Every other website in those answers is measured the same way, so the
                                    numbers below can be compared directly.
                                </span>
                            }
                            type="info"
                            showIcon
                        />
                        <SearchPromptsList
                            domain={domain}
                            assistantLabel={assistant.label}
                            niche={domainVisibilityAnalysis.industry_niche}
                            searchPrompts={domainVisibilityAnalysis.search_prompts}
                        />
                    </Col>

                    <Col span={6} className="top-websites-column">
                        <PresenceScore
                            presenceScore={domainVisibilityAnalysis.presence_score}
                        />

                        <Affix offsetTop={70} className="sticky">
                            <TopWebsites
                                domain={domain}
                                topWebsitePerformances={domainVisibilityAnalysis.top_websites}
                            />
                        </Affix>
                    </Col>
                </Row>
            )}
        </Card>
    );
}